import { formatLocalizedString } from "@glide/localization";
import { AppKind } from "@glide/location-common";
import { ignore } from "@glide/support";

export enum TimeSeriesInterval {
    Hour = "hour",
    Day = "day",
    Week = "week",
    Month = "month",
    Quarter = "quarter",
    Year = "year",
}

export enum TimeSeriesRelativeDuration {
    Last24Hours = "last-24-hours",
    Last7Days = "last-7-days",
    Last30Days = "last-30-days",
    Last90Days = "last-90-days",
    Last12Months = "last-12-months",
    Yesterday = "yesterday",
    LastWeek = "last-week",
    LastMonth = "last-month",
    LastQuarter = "last-quarter",
    LastYear = "last-year",
    DayToNow = "day-to-now",
    WeekToDate = "week-to-date",
    MonthToDate = "month-to-date",
    QuarterToDate = "quarter-to-date",
    YearToDate = "year-to-date",
}

export const ALL_TIME_SERIES_RELATIVE_DURATION = [
    TimeSeriesRelativeDuration.Last24Hours,
    TimeSeriesRelativeDuration.Last7Days,
    TimeSeriesRelativeDuration.Last30Days,
    TimeSeriesRelativeDuration.Last90Days,
    TimeSeriesRelativeDuration.Last12Months,
    TimeSeriesRelativeDuration.Yesterday,
    TimeSeriesRelativeDuration.LastWeek,
    TimeSeriesRelativeDuration.LastMonth,
    TimeSeriesRelativeDuration.LastQuarter,
    TimeSeriesRelativeDuration.LastYear,
    TimeSeriesRelativeDuration.DayToNow,
    TimeSeriesRelativeDuration.WeekToDate,
    TimeSeriesRelativeDuration.MonthToDate,
    TimeSeriesRelativeDuration.QuarterToDate,
    TimeSeriesRelativeDuration.YearToDate,
] as const;

// This is checking if we're missing an enum in ALL_TIME_SERIES_RELATIVE_DURATION
type _MissingDurations = Exclude<TimeSeriesRelativeDuration, (typeof ALL_TIME_SERIES_RELATIVE_DURATION)[number]>;
const _ensureAllDurations: _MissingDurations extends never ? true : _MissingDurations = true;
// This is just to make ts happy
ignore(_ensureAllDurations);

export function isTimeSeriesRelativeDuration(x: unknown): x is TimeSeriesRelativeDuration {
    return ALL_TIME_SERIES_RELATIVE_DURATION.some(duration => duration === x);
}

export function localizeTimeSeriesRelativeDuration(unlocalizedTimeSeries: TimeSeriesRelativeDuration): string {
    switch (unlocalizedTimeSeries) {
        case TimeSeriesRelativeDuration.Last24Hours:
            return formatLocalizedString("lastNHours", ["24"], AppKind.Page);
        case TimeSeriesRelativeDuration.Last7Days:
            return formatLocalizedString("lastNDays", ["7"], AppKind.Page);
        case TimeSeriesRelativeDuration.Last30Days:
            return formatLocalizedString("lastNDays", ["30"], AppKind.Page);
        case TimeSeriesRelativeDuration.Last90Days:
            return formatLocalizedString("lastNDays", ["90"], AppKind.Page);
        case TimeSeriesRelativeDuration.Last12Months:
            return formatLocalizedString("lastNMonths", ["12"], AppKind.Page);
        case TimeSeriesRelativeDuration.Yesterday:
            return formatLocalizedString("yesterday", [], AppKind.Page);
        case TimeSeriesRelativeDuration.LastWeek:
            return formatLocalizedString("lastWeek", [], AppKind.Page);
        case TimeSeriesRelativeDuration.LastMonth:
            return formatLocalizedString("lastMonth", [], AppKind.Page);
        case TimeSeriesRelativeDuration.LastQuarter:
            return formatLocalizedString("lastQuarter", [], AppKind.Page);
        case TimeSeriesRelativeDuration.LastYear:
            return formatLocalizedString("lastYear", [], AppKind.Page);
        case TimeSeriesRelativeDuration.DayToNow:
            return formatLocalizedString("dayToNow", [], AppKind.Page);
        case TimeSeriesRelativeDuration.WeekToDate:
            return formatLocalizedString("weekToDate", [], AppKind.Page);
        case TimeSeriesRelativeDuration.MonthToDate:
            return formatLocalizedString("monthToDate", [], AppKind.Page);
        case TimeSeriesRelativeDuration.QuarterToDate:
            return formatLocalizedString("quarterToDate", [], AppKind.Page);
        case TimeSeriesRelativeDuration.YearToDate:
            return formatLocalizedString("yearToDate", [], AppKind.Page);
    }
}
